import {Injectable, Optional, SkipSelf} from "@angular/core";
import {BdpkConfigService} from "../../bdpk-common/services/bdpk-config.service";
import {DxHttpDispatcher} from "../../dx-core/services/dx-http-dispatcher.service";
import {Observable} from "rxjs";
import {fromPromise} from "rxjs/internal-compatibility";
import {HttpHeaders, HttpParams, HttpResponse} from "@angular/common/http";
import {PitAgreementTask} from "../../pit-agreement/domain/pit-agreement-task";
import {map, take} from "rxjs/operators";
import {DxRequestOptions} from "../../dx-core/domain/http/dx-request-options";
import {DxObjectMapper} from "../../dx-core/services/dx-object-mapper.service";
import {DxControlPointChainInstance} from "../domain/control-points/dx-control-point-chain-instance";
import {DxControlPointChainGeneration} from "../domain/control-points/dx-control-point-chain-generation";
import {DxControlPointInstance} from "../domain/control-points/dx-control-point-instance";
import {PitBpmProcessInstance} from "../domain/pit-bpm-process-instance";
import {ClientHandlerExtra} from "../../dx-core/domain/http/client-handler-extra";

/**
 *
 * Сервия для работы с REST-API комунды.
 */
@Injectable()
export class PitBpmRestService {

  private bpmContext: string;
  private bpmOptions: DxRequestOptions = {
    headers: new HttpHeaders().set('Content-Type', 'application/json;charset=utf-8'),
    observe: "response"
  };

  /**
   * добавил проверку на использование как singleton, в качестве индикатора
   */
  constructor (@Optional() @SkipSelf() private self: PitBpmRestService,
               private _configService: BdpkConfigService,
               private _mapper: DxObjectMapper,
               private _http: DxHttpDispatcher) {
    if (self) {
      throw new Error(
        'PitBpmRestService is already loaded. Import it in the AppModule as PitSharedModule only once');
    }
    this.bpmContext = this._configService.pitBackendBPMEndPoint;
  }

  getTasksOfProcess(processInstanceId: number): Observable<PitAgreementTask[]> {
    return this._http.get(`${this.bpmContext}/process-instances/${processInstanceId}/task-instances`, this.bpmOptions)
      .pipe(take(1),
            map((response: HttpResponse<any>) => {
              return this._mapper.deserializeASArray<PitAgreementTask>(response.body, PitAgreementTask);
            }));
  }

  startProcess(businessObjectTypeId, businessObjectId, processTemplateId: number, handlerExtra?: ClientHandlerExtra): Observable<PitBpmProcessInstance> {
    const promise = new Promise<any>((resolve, reject) => {
      // const request = {businessObjectTypeId, businessObjectId, processTemplateId};
      const request = {processTemplateId, businessObject: {id: businessObjectId, typeId: businessObjectTypeId}};
      const body = JSON.stringify(request);
      const options = handlerExtra ? handlerExtra.applyToOptions(this.bpmOptions) : this.bpmOptions;

      this._http.post(`${this.bpmContext}/process-instances`, body, options).subscribe(response => {
          console.log(response);
          const procInstance = this._mapper.deserialize(response.body, PitBpmProcessInstance);
          resolve(procInstance);
        }, errorResponse => {
          console.log(errorResponse);
          reject(DxHttpDispatcher.handleHttpError(errorResponse));
        }
      )
    });
    return fromPromise(promise);
  }

  /** останавливает активный процесс по Id */
  cancelProcess(processId: number, handlerExtra?: ClientHandlerExtra): Observable<boolean> {
    const promise = new Promise<any>((resolve, reject) => {
      const endPoint = this.bpmContext+'/process-instances/'+processId.toString()+'/cancellations';
      const body = '{}';
      const options = handlerExtra ? handlerExtra.applyToOptions(this.bpmOptions) : this.bpmOptions;

      this._http.post(endPoint, body, options).subscribe(response => {
          resolve(true);
        }, errorResponse => {
          reject(DxHttpDispatcher.handleHttpError(errorResponse));
        }
      )
    });
    return fromPromise(promise);
  }


  controlPointInstances(objectId: number, objectTypeId: number, responsibleId: number): Observable<DxControlPointInstance[]> {

    return this._http.get(`${this.bpmContext}/control-point-instances`, {...this.bpmOptions,
                          params: new HttpParams().set('objectId', String(objectId))
                                                  .set('objectTypeId', String(objectTypeId))
                                                  .set(responsibleId ? 'responsibleId' : 'noResponsibleId', String(responsibleId))} as DxRequestOptions)
      .pipe(take(1),
        map((response: HttpResponse<any>) => {
          return this._mapper.deserializeASArray(response.body, DxControlPointInstance);
        }));
  }

  controlPointChainInstancesGeneration(input: DxControlPointChainGeneration): Observable<DxControlPointChainInstance> {
    const body = this._mapper.serialize(input, DxControlPointChainGeneration);
    return this._http.post(`${this.bpmContext}/control-point-chain-instances/generation`, body, this.bpmOptions)
      .pipe(take(1),
        map((response: HttpResponse<any>) => {
          return this._mapper.deserialize(response.body, DxControlPointChainInstance);
        }));
  }

  controlPointChainInstancesRedistribution(chainId: number, redistributionDate: Date): Observable<DxControlPointChainInstance> {
    const body = JSON.stringify({redistributionDate: (this._mapper.serialize(redistributionDate, Date) as string)['replaceAll']('"', '')});
    return this._http.post(`${this.bpmContext}/control-point-chain-instances/${chainId}/redistribution`, body, this.bpmOptions)
      .pipe(take(1),
        map((response: HttpResponse<any>) => {
          return this._mapper.deserialize(response.body, DxControlPointChainInstance);
        }));
  }

  processInstances(objectTypeId: number, objectIds?: number[]): Observable<PitBpmProcessInstance[]> {
    let params: HttpParams = new HttpParams().set('objectTypeId', String(objectTypeId))
                                             .set('isActive', 'true')
                                             .set(objectIds ? 'objectIds': 'noObjectIds', objectIds.join(','));
    return this._http.get(`${this.bpmContext}/process-instances`, {...this.bpmOptions, params: params} as DxRequestOptions)
      .pipe(map((response: HttpResponse<any>) => {
        return this._mapper.deserializeASArray(response.body, PitBpmProcessInstance);
      }));
  }

}
